// pages/SupplyDemandPage.js - 供需管理
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Statistic, Progress, Tag } from 'antd';
import { ThunderboltOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', solar: '#f4c542', storage: '#52e8b0', load: '#ff5a7f', grid: '#3a8dde', text: '#fff', muted: 'rgba(255,255,255,0.6)' };

const SupplyDemandPage = () => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState({
    todayGeneration: 0, todayPv: 0, todayWind: 0, todayStorage: 0, todayDiesel: 0, todayGrid: 0,
    monthGeneration: 0, totalDevice: 0, onlineDevice: 0
  });
  const [realtime, setRealtime] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getPowerOverview().then(json => { if (json.success) setOverview(json); }),
      api.getPowerLatest().then(json => { if (json.success) setRealtime(json.data || []); }),
    ]).finally(() => setLoading(false));
  }, []);

  const supply = parseFloat(overview.todayPv || 0) + parseFloat(overview.todayWind || 0);
  const demand = parseFloat(overview.todayStorage || 0) + parseFloat(overview.todayDiesel || 0);
  const balance = supply - demand;
  const balancePct = supply > 0 ? Math.min(100, Math.round((demand / supply) * 100)) : 0;

  const supplyDevices = realtime.filter(d => d.type === 'photovoltaic' || d.type === 'battery');

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: t('supplyDemand.todayGeneration'), value: parseFloat(overview.todayGeneration || 0).toFixed(1), unit: 'kWh', color: COLORS.primary },
          { label: t('supplyDemand.pvGeneration'), value: parseFloat(overview.todayPv || 0).toFixed(1), unit: 'kWh', color: COLORS.solar },
          { label: t('supplyDemand.storageDischarge'), value: parseFloat(overview.todayStorage || 0).toFixed(1), unit: 'kWh', color: COLORS.storage },
          { label: t('supplyDemand.onlineDevice'), value: `${overview.onlineDevice || 0}`, unit: `/ ${overview.totalDevice || 0} ${t('supplyDemand.unit')}`, color: '#52c41a' },
        ].map(item => (
          <Col xs={24} sm={12} lg={6} key={item.label}>
            <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(0,180,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,180,216,0.3)' }}>
                  <ThunderboltOutlined style={{ fontSize: 22, color: item.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{item.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: item.color }}>{item.value} <span style={{ fontSize: 12, color: COLORS.muted }}>{item.unit}</span></div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #52c41a', marginBottom: 16 }}>{t('supplyDemand.supplySide')}</div>
            <Statistic title={<span style={{ color: COLORS.muted }}>{t('supplyDemand.currentSupplyPower')}</span>} value={supply.toFixed(1)} suffix="kW"
              prefix={<ArrowUpOutlined style={{ color: '#52c41a' }} />}
              styles={{ body: { color: '#52c41a', fontSize: 28 } }} />
            <div style={{ marginTop: 16 }}>
              {supplyDevices.length > 0 ? supplyDevices.slice(0, 4).map(d => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ color: COLORS.muted, fontSize: 12 }}>{d.device_name}</span>
                  <Tag color="green">{parseFloat(d.power || 0).toFixed(1)} kW</Tag>
                </div>
              )) : (
                <div style={{ textAlign: 'center', color: COLORS.muted, fontSize: 12, padding: '12px 0' }}>{t('supplyDemand.noSupplyDevice')}</div>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #ff5a7f', marginBottom: 16 }}>{t('supplyDemand.demandSide')}</div>
            <Statistic title={<span style={{ color: COLORS.muted }}>{t('supplyDemand.currentDemandPower')}</span>} value={demand.toFixed(1)} suffix="kW"
              prefix={<ArrowDownOutlined style={{ color: '#ff5a7f' }} />}
              styles={{ body: { color: '#ff5a7f', fontSize: 28 } }} />
            <div style={{ marginTop: 16 }}>
              {realtime.filter(d => d.type === 'charger' || d.type === 'generator').length > 0 ? realtime.filter(d => d.type === 'charger' || d.type === 'generator').slice(0, 4).map(d => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ color: COLORS.muted, fontSize: 12 }}>{d.device_name}</span>
                  <Tag color="orange">{parseFloat(d.power || 0).toFixed(1)} kW</Tag>
                </div>
              )) : (
                <div style={{ textAlign: 'center', color: COLORS.muted, fontSize: 12, padding: '12px 0' }}>{t('supplyDemand.noDemandDevice')}</div>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #3a8dde', marginBottom: 16 }}>{t('supplyDemand.balanceStatus')}</div>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Progress type="circle" percent={balancePct} format={p => `${p}%`}
                strokeColor={balance >= 0 ? '#52c41a' : '#ff4d4f'}
                trailColor="rgba(255,255,255,0.1)"
              />
              <div style={{ marginTop: 16 }}>
                <Statistic title={<span style={{ color: COLORS.muted }}>{t('supplyDemand.surplus')}</span>} value={balance.toFixed(1)} suffix="kW"
                  styles={{ body: { color: balance >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 22 } }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #52e8b0', marginBottom: 16 }}>{t('supplyDemand.storageStatus')}</div>
            <Row gutter={[16, 16]}>
              {realtime.filter(d => d.type === 'battery').length > 0 ? realtime.filter(d => d.type === 'battery').map(d => (
                <Col xs={24} sm={12} md={8} key={d.id}>
                  <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 16, border: '1px solid rgba(82,232,176,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ color: COLORS.text, fontWeight: 600 }}>{d.device_name}</span>
                      <Tag color={d.device_status === 1 ? 'green' : 'default'}>{d.device_status === 1 ? t('supplyDemand.running') : t('status.offline')}</Tag>
                    </div>
                    <Row gutter={[8, 8]}>
                      <Col span={8}>
                        <div style={{ fontSize: 10, color: COLORS.muted }}>{t('supplyDemand.soc')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.storage }}>{d.soc ? `${d.soc}%` : '-'}</div>
                      </Col>
                      <Col span={8}>
                        <div style={{ fontSize: 10, color: COLORS.muted }}>{t('supplyDemand.power')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.primary }}>{parseFloat(d.power || 0).toFixed(1)} kW</div>
                      </Col>
                      <Col span={8}>
                        <div style={{ fontSize: 10, color: COLORS.muted }}>{t('supplyDemand.temperature')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.muted }}>{d.temperature ? `${d.temperature}°C` : '-'}</div>
                      </Col>
                    </Row>
                    {d.soc && <Progress percent={parseFloat(d.soc)} size="small" strokeColor="#52e8b0" trailColor="rgba(255,255,255,0.1)" />}
                  </div>
                </Col>
              )) : (
                <Col span={24}>
                  <div style={{ textAlign: 'center', color: COLORS.muted, fontSize: 13, padding: '24px 0' }}>{t('supplyDemand.noStorageData')}</div>
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SupplyDemandPage;
